import type { Project } from '@shared/types'

/** Same cap as the TN rename flow (Dashboard / Home card rename inputs). */
export const GCC_NAME_MAX_LENGTH = 100

/** Fixed browser-tab title for GCC pages that are not tied to a single design. */
export const GCC_APP_TITLE = 'Global Capability Center'

/**
 * Display name of a GCC design. `name` is the editable display name; `client` stays the
 * client company (it feeds TN Analyses and the HTML export), so it is only a fallback for
 * projects saved without a name.
 */
export function gccDisplayName(project: Pick<Project, 'name' | 'client'>): string {
  const name = project.name?.trim()
  if (name) return name
  return project.client ? `${project.client} GCC Design` : 'GCC Design'
}

/** Browser-tab title while a GCC dashboard is open. */
export function gccDashboardDocumentTitle(project: Pick<Project, 'name' | 'client'>): string {
  return `${gccDisplayName(project)} · ${GCC_APP_TITLE}`
}

/**
 * Only the owner may rename. Desktop is single-user (always owner); on web mirror the TN
 * rename gate exactly: createdBy === session email.
 */
export function canRenameGccProject(
  platform: 'desktop' | 'web',
  sessionEmail: string,
  project: Pick<Project, 'createdBy'>,
): boolean {
  return platform === 'desktop' || (!!sessionEmail && project.createdBy === sessionEmail)
}

/** Returns an inline validation message, or null when the name is acceptable. */
export function validateGccName(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'Name cannot be empty'
  if (trimmed.length > GCC_NAME_MAX_LENGTH) return `Name must be ${GCC_NAME_MAX_LENGTH} characters or fewer`
  return null
}
